import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminPanel.css';
import { API_URL } from '../config';

function AdminPanel({ onLogout }) {
  const [businesses, setBusinesses] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('businesses');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: '',
    businessId: '',
    environment: 'test'
  });

  // New business form state
  const [newBusiness, setNewBusiness] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    emailFrequency: 'Quarterly',
    initialCredits: 100 // Default initial credits
  });

  // New user form state
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'user',
    businessId: '',
    environment: 'test'
  });

  // Add this to your state declarations
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [businessFormData, setBusinessFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    emailFrequency: 'Quarterly',
    availableCredits: 0,
    usedCredits: 0,
    can_run_manual_checks: false
  });

  // Add to existing state declarations
  const [checks, setChecks] = useState([]);

  // Add these new state variables for notification testing
  const [notificationType, setNotificationType] = useState('SYSTEM_UPDATE');
  const [selectedBusinessId, setSelectedBusinessId] = useState('');
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [notificationResult, setNotificationResult] = useState(null);
  const [notificationLoading, setNotificationLoading] = useState(false);

  // Move axiosAuth outside of component or use useMemo
  const axiosAuth = React.useMemo(() => axios.create({
    baseURL: API_URL,
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
    }
  }), []); // Empty dependency array as we only need to create this once

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      if (!mounted) return;
      
      setLoading(true);
      try {
        const [businessesRes, usersRes, checksRes] = await Promise.all([
          axiosAuth.get('/api/admin/businesses'),
          axiosAuth.get('/api/admin/users'),
          axiosAuth.get('/api/admin/checks')
        ]);

        if (mounted) {
          setBusinesses(businessesRes.data);
          setUsers(usersRes.data);
          setChecks(checksRes.data);
          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setError('Failed to load data');
          if (err.response?.status === 401) {
            onLogout();
          }
        }
      }
    };

    fetchData();

    // Cleanup function
    return () => {
      mounted = false;
    };
  }, [axiosAuth, onLogout]); // Minimal dependencies

  const handleAddBusiness = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosAuth.post('/api/admin/businesses', {
        name: newBusiness.name,
        email: newBusiness.email,
        phone: newBusiness.phone,
        address: newBusiness.address,
        emailFrequency: newBusiness.emailFrequency,
        available_credits: newBusiness.initialCredits,
        used_credits: 0
      });
      
      setBusinesses([...businesses, response.data]);
      setNewBusiness({
        name: '',
        email: '',
        phone: '',
        address: '',
        emailFrequency: 'Quarterly',
        initialCredits: 100
      });
    } catch (err) {
      setError('Failed to add business');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosAuth.post('/api/admin/users', newUser);
      setUsers([...users, response.data]);
      setNewUser({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        role: 'user',
        businessId: '',
        environment: 'test'
      });
    } catch (err) {
      setError('Failed to add user');
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await axiosAuth.delete(`/api/admin/users/${userId}`);
        setUsers(users.filter(user => user.id !== userId));
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      firstName: user.first_name,
      lastName: user.last_name,
      role: user.role,
      businessId: user.business_id,
      environment: user.environment || 'test'
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosAuth.put(`/api/admin/users/${editingUser.id}`, formData);
      setUsers(users.map(user => 
        user.id === editingUser.id ? response.data : user
      ));
      setEditingUser(null);
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        role: '',
        businessId: '',
        environment: 'test'
      });
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDeleteBusiness = async (businessId) => {
    if (window.confirm('Are you sure you want to delete this business? This will also delete all associated users.')) {
      try {
        await axiosAuth.delete(`/api/admin/businesses/${businessId}`);
        setBusinesses(businesses.filter(business => business.id !== businessId));
        // Also remove associated users from the users state
        setUsers(users.filter(user => user.business_id !== businessId));
      } catch (error) {
        console.error('Error deleting business:', error);
        setError('Failed to delete business');
      }
    }
  };

  const handleEditBusiness = (business) => {
    setEditingBusiness(business);
    setBusinessFormData({
      name: business.name,
      email: business.email,
      phone: business.phone || '',
      address: business.address || '',
      emailFrequency: business.email_frequency || 'Quarterly',
      availableCredits: business.available_credits || 0,
      usedCredits: business.used_credits || 0,
      can_run_manual_checks: business.can_run_manual_checks || false
    });
  };

  const handleUpdateBusiness = async (e) => {
    e.preventDefault();
    try {
      // Log the data being sent for debugging
      console.log('Updating business with data:', {
        name: businessFormData.name,
        email: businessFormData.email,
        phone: businessFormData.phone,
        address: businessFormData.address,
        email_frequency: businessFormData.emailFrequency,
        available_credits: parseInt(businessFormData.availableCredits),
        used_credits: parseInt(businessFormData.usedCredits),
        can_run_manual_checks: businessFormData.can_run_manual_checks || false
      });

      // Update business details including credits
      const response = await axiosAuth.put(`/api/admin/businesses/${editingBusiness.id}`, {
        name: businessFormData.name,
        email: businessFormData.email,
        phone: businessFormData.phone,
        address: businessFormData.address,
        email_frequency: businessFormData.emailFrequency,
        available_credits: parseInt(businessFormData.availableCredits),
        used_credits: parseInt(businessFormData.usedCredits),
        can_run_manual_checks: businessFormData.can_run_manual_checks || false
      });

      if (response.data) {
        // Update the businesses state with the new data
        setBusinesses(businesses.map(business => 
          business.id === editingBusiness.id ? response.data : business
        ));
        
        // Reset form and editing state
        setEditingBusiness(null);
        setBusinessFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          emailFrequency: 'Quarterly',
          availableCredits: 0,
          usedCredits: 0,
          can_run_manual_checks: false
        });
      }
    } catch (error) {
      console.error('Error updating business:', error);
      // More detailed error message
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
      setError(`Failed to update business: ${errorMessage}`);
    }
  };

  const handleBusinessFormChange = (e) => {
    setBusinessFormData({
      ...businessFormData,
      [e.target.name]: e.target.value
    });
  };

  // Add this helper function to group checks by business
  const getChecksByBusiness = () => {
    const grouped = {};
    businesses.forEach(business => {
      const businessChecks = checks.filter(check => check.business_id === business.id);
      // Only count LIVE environment checks for cost
      const liveChecks = businessChecks.filter(check => 
        check.user_environment === 'live' || check.user_environment === 'LIVE'
      );
      grouped[business.id] = {
        checks: businessChecks,
        totalChecks: businessChecks.length,
        liveChecks: liveChecks.length,
        // Calculate cost only for live checks at £0.60 each
        totalCost: (liveChecks.length * 0.60).toFixed(2)
      };
    });
    return grouped;
  };

  // Optional: Add a display formatter function for the UI
  const formatFrequencyDisplay = (frequency) => {
    const displayMap = {
      'Quarterly': 'Quarterly (Every 3 Months)',
      'Biannual': 'Biannual (Every 6 Months)',
      'Yearly': 'Yearly'
    };
    return displayMap[frequency] || frequency;
  };

  // Add this function to fetch staff for a selected business
  const fetchStaffForBusiness = async (businessId) => {
    if (!businessId) {
      setStaffList([]);
      return;
    }
    
    try {
      const response = await axiosAuth.get(`/api/admin/businesses/${businessId}/staff`);
      if (response.data) {
        setStaffList(response.data);
      }
    } catch (error) {
      console.error('Error fetching staff:', error);
      setStaffList([]);
    }
  };

  // Add this function to handle business selection change
  const handleBusinessChange = (e) => {
    const businessId = e.target.value;
    setSelectedBusinessId(businessId);
    setSelectedStaffId(''); // Reset staff selection
    fetchStaffForBusiness(businessId);
  };

  // Add this function to create a test notification
  const createTestNotification = async (e) => {
    e.preventDefault();
    
    if (!selectedBusinessId) {
      setNotificationResult({
        success: false,
        message: 'Please select a business'
      });
      return;
    }
    
    try {
      setNotificationLoading(true);
      setNotificationResult(null);
      
      const response = await axiosAuth.post('/api/admin/notifications/test', {
        businessId: selectedBusinessId,
        staffId: selectedStaffId || undefined,
        type: notificationType
      });
      
      setNotificationResult({
        success: true,
        message: `Test notification created successfully: ${response.data.message || ''}`
      });
      
    } catch (error) {
      console.error('Error creating test notification:', error);
      setNotificationResult({
        success: false,
        message: error.response?.data?.error || 'Failed to create notification'
      });
    } finally {
      setNotificationLoading(false);
    }
  };

  // Add a separate function for adding credits
  const handleAddCredits = async (amount) => {
    try {
      const newTotal = parseInt(businessFormData.availableCredits) + amount;
      setBusinessFormData({
        ...businessFormData,
        availableCredits: newTotal
      });
    } catch (error) {
      console.error('Error adding credits:', error);
      setError('Failed to add credits: ' + error.message);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <h1>System Administration</h1>
        <button onClick={onLogout} className="logout-btn">Logout</button>
      </header>

      <nav className="admin-nav">
        <button 
          className={`tab-btn ${activeTab === 'businesses' ? 'active' : ''}`}
          onClick={() => setActiveTab('businesses')}
        >
          Businesses
        </button>
        <button 
          className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button 
          className={`tab-btn ${activeTab === 'reports' ? 'active' : ''}`}
          onClick={() => setActiveTab('reports')}
        >
          Reports
        </button>
        {/* Temporarily hiding the notifications tab
        <button 
          className={`tab-btn ${activeTab === 'notifications' ? 'active' : ''}`}
          onClick={() => setActiveTab('notifications')}
        >
          Notifications
        </button>
        */}
      </nav>

      <div className="admin-content">
        {activeTab === 'businesses' ? (
          <div className="businesses-section">
            <h2>Add New Business</h2>
            <form onSubmit={handleAddBusiness} className="add-form">
              <input
                type="text"
                placeholder="Business Name"
                value={newBusiness.name}
                onChange={(e) => setNewBusiness({...newBusiness, name: e.target.value})}
                required
              />
              <input
                type="email"
                placeholder="Business Email"
                value={newBusiness.email}
                onChange={(e) => setNewBusiness({...newBusiness, email: e.target.value})}
                required
              />
              <input
                type="tel"
                placeholder="Phone"
                value={newBusiness.phone}
                onChange={(e) => setNewBusiness({...newBusiness, phone: e.target.value})}
              />
              <textarea
                placeholder="Address"
                value={newBusiness.address}
                onChange={(e) => setNewBusiness({...newBusiness, address: e.target.value})}
              />
              <select
                value={newBusiness.emailFrequency}
                onChange={(e) => setNewBusiness({...newBusiness, emailFrequency: e.target.value})}
                required
              >
                <option value="Quarterly">Quarterly</option>
                <option value="Biannual">Biannual</option>
                <option value="Yearly">Yearly</option>
              </select>
              <div className="form-group">
                <label>Initial Credits</label>
                <div className="credits-input-group">
                  <input
                    type="number"
                    min="0"
                    placeholder="Initial Credits"
                    value={newBusiness.initialCredits}
                    onChange={(e) => setNewBusiness({
                      ...newBusiness, 
                      initialCredits: parseInt(e.target.value) || 0
                    })}
                    required
                  />
                  <div className="quick-credit-buttons">
                    <button 
                      type="button" 
                      onClick={() => setNewBusiness(prev => ({
                        ...prev, 
                        initialCredits: 100
                      }))}
                    >
                      100
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setNewBusiness(prev => ({
                        ...prev, 
                        initialCredits: 500
                      }))}
                    >
                      500
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setNewBusiness(prev => ({
                        ...prev, 
                        initialCredits: 1000
                      }))}
                    >
                      1000
                    </button>
                  </div>
                </div>
              </div>
              <button type="submit">Add Business</button>
            </form>

            {editingBusiness && (
              <div className="edit-form">
                <h3>Edit Business</h3>
                <form onSubmit={handleUpdateBusiness}>
                  <div>
                    <label>Business Name:</label>
                    <input
                      type="text"
                      name="name"
                      value={businessFormData.name}
                      onChange={handleBusinessFormChange}
                      required
                    />
                  </div>
                  <div>
                    <label>Email:</label>
                    <input
                      type="email"
                      name="email"
                      value={businessFormData.email}
                      onChange={handleBusinessFormChange}
                      required
                    />
                  </div>
                  <div>
                    <label>Phone:</label>
                    <input
                      type="tel"
                      name="phone"
                      value={businessFormData.phone}
                      onChange={handleBusinessFormChange}
                    />
                  </div>
                  <div>
                    <label>Address:</label>
                    <textarea
                      name="address"
                      value={businessFormData.address}
                      onChange={handleBusinessFormChange}
                    />
                  </div>
                  <div>
                    <label>Email Frequency:</label>
                    <select
                      name="emailFrequency"
                      value={businessFormData.emailFrequency}
                      onChange={handleBusinessFormChange}
                      required
                    >
                      <option value="Quarterly">Quarterly</option>
                      <option value="Biannual">Biannual</option>
                      <option value="Yearly">Yearly</option>
                    </select>
                  </div>
                  <div className="credits-management">
                    <h4>Credits Overview</h4>
                    <div className="credits-info">
                      <div className="credits-stat">
                        <div className="credits-label">Available Credits</div>
                        <input
                          type="number"
                          name="availableCredits"
                          value={businessFormData.availableCredits}
                          onChange={handleBusinessFormChange}
                          min="0"
                          className="credits-input"
                        />
                      </div>
                      <div className="credits-stat">
                        <div className="credits-label">Used Credits</div>
                        <div className="credits-value">{businessFormData.usedCredits}</div>
                      </div>
                      <div className="credits-stat">
                        <div className="credits-label">Remaining Credits</div>
                        <div className="credits-value">
                          {businessFormData.availableCredits - businessFormData.usedCredits}
                        </div>
                      </div>
                    </div>
                    <div className="credits-actions">
                      <div className="quick-credit-buttons">
                        <button 
                          type="button" 
                          onClick={() => handleAddCredits(100)}
                          className="add-credits-btn"
                        >
                          Add 100
                        </button>
                        <button 
                          type="button" 
                          onClick={() => handleAddCredits(500)}
                          className="add-credits-btn"
                        >
                          Add 500
                        </button>
                        <button 
                          type="button" 
                          onClick={() => handleAddCredits(1000)}
                          className="add-credits-btn"
                        >
                          Add 1000
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={businessFormData.can_run_manual_checks || false}
                        onChange={(e) => setBusinessFormData({
                          ...businessFormData,
                          can_run_manual_checks: e.target.checked
                        })}
                      />
                      Allow Manual Checks
                    </label>
                    <small className="form-text text-muted">
                      If enabled, users can perform on-demand DVLA checks for staff members.
                    </small>
                  </div>
                  <div className="form-buttons">
                    <button type="submit">Save Changes</button>
                    <button type="button" onClick={() => setEditingBusiness(null)}>Cancel</button>
                  </div>
                </form>
              </div>
            )}

            <h2>Existing Businesses</h2>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Email Frequency</th>
                    <th>Available Credits</th>
                    <th>Used Credits</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {businesses.map(business => (
                    <tr key={business.id}>
                      <td data-label="Name">{business.name}</td>
                      <td data-label="Email">{business.email}</td>
                      <td data-label="Phone">{business.phone}</td>
                      <td data-label="Email Frequency">{formatFrequencyDisplay(business.email_frequency)}</td>
                      <td data-label="Available Credits">{business.available_credits}</td>
                      <td data-label="Used Credits">{business.used_credits}</td>
                      <td data-label="Actions">
                        <button 
                          className="edit-btn"
                          onClick={() => handleEditBusiness(business)}
                        >
                          Edit
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => handleDeleteBusiness(business.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : activeTab === 'users' ? (
          <div className="users-section">
            <h2>Add New User</h2>
            <form onSubmit={handleAddUser} className="add-form">
              <select
                value={newUser.businessId}
                onChange={(e) => setNewUser({...newUser, businessId: e.target.value})}
                required
              >
                <option value="">Select Business</option>
                {businesses.map(business => (
                  <option key={business.id} value={business.id}>
                    {business.name}
                  </option>
                ))}
              </select>
              <input
                type="email"
                placeholder="Email"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={newUser.password}
                onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                required
              />
              <input
                type="text"
                placeholder="First Name"
                value={newUser.firstName}
                onChange={(e) => setNewUser({...newUser, firstName: e.target.value})}
              />
              <input
                type="text"
                placeholder="Last Name"
                value={newUser.lastName}
                onChange={(e) => setNewUser({...newUser, lastName: e.target.value})}
              />
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                required
              >
                <option value="user">User</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
              <select
                value={newUser.environment}
                onChange={(e) => setNewUser({...newUser, environment: e.target.value})}
                required
              >
                <option value="test">Test Mode</option>
                <option value="live">Live Mode</option>
              </select>
              <button type="submit">Add User</button>
            </form>

            <h2>Existing Users</h2>
            <div className="table-container">
              {editingUser && (
                <div className="edit-form">
                  <h3>Edit User</h3>
                  <form onSubmit={handleUpdate}>
                    <div>
                      <label>Email:</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <label>First Name:</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <label>Last Name:</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <label>Role:</label>
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Role</option>
                        <option value="admin">Admin</option>
                        <option value="manager">Manager</option>
                        <option value="user">User</option>
                      </select>
                    </div>
                    <div>
                      <label>Business:</label>
                      <select
                        name="businessId"
                        value={formData.businessId}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Business</option>
                        {businesses.map(business => (
                          <option key={business.id} value={business.id}>
                            {business.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label>Environment:</label>
                      <select
                        name="environment"
                        value={formData.environment}
                        onChange={handleChange}
                        required
                      >
                        <option value="test">Test Mode</option>
                        <option value="live">Live Mode</option>
                      </select>
                    </div>
                    <div className="form-buttons">
                      <button type="submit">Save Changes</button>
                      <button type="button" onClick={() => setEditingUser(null)}>Cancel</button>
                    </div>
                  </form>
                </div>
              )}
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Business</th>
                    <th>Environment</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td data-label="Name">{`${user.first_name || ''} ${user.last_name || ''}`}</td>
                      <td data-label="Email">{user.email}</td>
                      <td data-label="Role">{user.role}</td>
                      <td data-label="Business">{businesses.find(b => b.id === user.business_id)?.name}</td>
                      <td data-label="Environment">
                        <span className={`environment-badge ${user.environment || 'test'}`}>
                          {(user.environment || 'test').toUpperCase()}
                        </span>
                      </td>
                      <td data-label="Actions">
                        {user.role !== 'owner' && (
                          <>
                            <button 
                              className="edit-btn"
                              onClick={() => handleEdit(user)}
                            >
                              Edit
                            </button>
                            <button 
                              className="delete-btn"
                              onClick={() => handleDelete(user.id)}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : activeTab === 'reports' ? (
          <div className="reports-section">
            <h2>DVLA Check Reports</h2>
            {businesses.map(business => {
              const businessData = getChecksByBusiness()[business.id] || { checks: [], totalChecks: 0, liveChecks: 0, totalCost: 0 };
              return (
                <div key={business.id} className="business-checks">
                  <h3>{business.name}</h3>
                  <div className="business-summary">
                    <p>Total Checks: {businessData.totalChecks}</p>
                    <p>Live Checks: {businessData.liveChecks}</p>
                    <p>Total Cost: £{businessData.totalCost}</p>
                  </div>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Check Type</th>
                          <th>License Number</th>
                          <th>Staff Member</th>
                          <th>Environment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {businessData.checks.map(check => {
                          const staff = users.find(u => u.id === check.staff_id);
                          return (
                            <tr key={check.id}>
                              <td>{new Date(check.check_date).toLocaleString()}</td>
                              <td>{check.check_type}</td>
                              <td>{check.driving_license_number}</td>
                              <td>{staff ? `${staff.first_name} ${staff.last_name}` : 'System'}</td>
                              <td>
                                <span className={`environment-badge ${check.user_environment || 'error'}`}>
                                  {(check.user_environment || 'ERROR').toUpperCase()}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="notifications-section">
            <h2>Notification Tester</h2>
            <p className="section-description">
              Create test notifications for any business to verify the notification system.
            </p>
            
            <form onSubmit={createTestNotification} className="notification-test-form">
              <div className="form-group">
                <label>Select Business:</label>
                <select 
                  value={selectedBusinessId} 
                  onChange={handleBusinessChange}
                  required
                >
                  <option value="">Select a Business</option>
                  {businesses.map(business => (
                    <option key={business.id} value={business.id}>
                      {business.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Notification Type:</label>
                <select 
                  value={notificationType} 
                  onChange={(e) => setNotificationType(e.target.value)}
                  required
                >
                  <option value="SYSTEM_UPDATE">System Update</option>
                  <option value="CHECK_LIMIT_WARNING">Check Limit Warning</option>
                  <option value="LICENSE_STATUS_CHANGE">License Status Change</option>
                  <option value="LICENSE_EXPIRING">License Expiring</option>
                  <option value="POINTS_CHANGE">Points Change</option>
                  <option value="NEW_STAFF">New Staff</option>
                  <option value="NEW_ENDORSEMENT">New Endorsement</option>
                  <option value="INACTIVE_STAFF">Inactive Staff</option>
                  <option value="HIGH_PRIORITY">High Priority Alert</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Staff Member (optional):</label>
                <select 
                  value={selectedStaffId} 
                  onChange={(e) => setSelectedStaffId(e.target.value)}
                >
                  <option value="">None (System Notification)</option>
                  {staffList.map(staff => (
                    <option key={staff.id} value={staff.id}>
                      {staff.first_name} {staff.last_name}
                    </option>
                  ))}
                </select>
              </div>
              
              <button 
                type="submit" 
                className="create-notification-btn"
                disabled={notificationLoading || !selectedBusinessId}
              >
                {notificationLoading ? 'Creating...' : 'Create Test Notification'}
              </button>
              
              {notificationResult && (
                <div className={`notification-result ${notificationResult.success ? 'success' : 'error'}`}>
                  {notificationResult.message}
                </div>
              )}
            </form>
            
            <div className="notification-info">
              <h3>Notification Types</h3>
              <ul className="notification-types-list">
                <li><strong>System Update</strong> - General system announcements</li>
                <li><strong>Check Limit Warning</strong> - Alerts when approaching monthly check limits</li>
                <li><strong>License Status Change</strong> - When a driver's license status changes</li>
                <li><strong>License Expiring</strong> - When a license is about to expire</li>
                <li><strong>Points Change</strong> - When penalty points change on a license</li>
                <li><strong>New Staff</strong> - When a new staff member is added</li>
                <li><strong>New Endorsement</strong> - When a new endorsement appears on a license</li>
                <li><strong>Inactive Staff</strong> - For staff with no recent license checks</li>
                <li><strong>High Priority</strong> - Critical alerts requiring immediate attention</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPanel; 